import {Manifest} from '../../../api/model/manifest';
import {WorkflowDefinition} from '../../../api/model/workflowDefinition';
import {buildManifestFromState, MyCheckBuilderState} from './my-checks-builder.model';

/**
 * Verlangt der Prüflauf beim Start eine Eingabe? Dieselbe Regel wie `ScheduleEligibilityChecker`
 * im Backend (`NOT_SCHEDULABLE_MANUAL_SELECTION`): maßgeblich ist `interactionRequired` an
 * irgendeinem Schritt, nicht der Auswahlmodus des Builders.
 */
export function requiresInteraction(manifest: Manifest | null | undefined): boolean {
  return (manifest?.steps ?? []).some(step => step.interactionRequired != null);
}

/**
 * Builder-Schritt, in dem die beim Start abgefragte Auswahl festgelegt wird: 1 = Regelauswahl,
 * 2 = Dateiauswahl, 0 = ein anderer Schritt fragt nach. `null`, wenn nichts abgefragt wird
 * ({@link requiresInteraction} ist dann `false`).
 */
export function interactiveBuilderStep(manifest: Manifest | null | undefined): number | null {
  const step = (manifest?.steps ?? []).find(s => s.interactionRequired != null);
  if (!step) return null;
  if (step.moduleId === 'rule_selection') return 1;
  if (step.moduleId === 'file_selection') return 2;
  return 0;
}

/**
 * Sperrt eine manuelle Regel- oder Dateiauswahl die Kachel „Zeitplan“ im Builder?
 *
 * <ul>
 *   <li>Mit ungespeicherten Änderungen zählt, was „Übernehmen“ schreiben würde
 *       ({@link buildManifestFromState}). Wer gerade auf manuelle Auswahl umstellt, kann also nicht
 *       mehr planen, obwohl der gespeicherte Stand es noch erlaubt.</li>
 *   <li>Ohne Änderungen zählt das gespeicherte Manifest, also derselbe Stand, den das Backend
 *       prüft. Der Builder-State allein taugt dafür nicht: hat das Manifest keinen Regel- bzw.
 *       Dateiauswahl-Schritt, bleiben `ruleSelectionMode`/`fileSelectionMode` auf dem Default
 *       `'manual'`, obwohl nichts abgefragt wird.</li>
 * </ul>
 *
 * <p>Maßgeblich bleibt die Vorschau im Popup; sie prüft den gespeicherten Stand im Backend.
 */
export function scheduleBlockedBySelection(
  state: MyCheckBuilderState,
  savedManifest: Manifest | null,
  dirty: boolean,
): boolean {
  const manifest = dirty || !savedManifest
    ? buildManifestFromState(state, savedManifest ?? undefined)
    : savedManifest;
  return requiresInteraction(manifest);
}

/**
 * Sperrt der gespeicherte Status des Prüflaufs die Kachel „Zeitplan“? Dieselbe Regel wie
 * `ScheduleEligibilityChecker` im Backend (`DEFINITION_NOT_ACTIVE`): planbar ist nur ein Prüflauf
 * im Status `ACTIVE`.
 *
 * <p>Maßgeblich ist immer der gespeicherte Status, auch bei ungespeicherten Änderungen: die Vorschau
 * im Popup prüft den Stand im Backend. Ein myCheck aus dem Builder wird beim Speichern `ACTIVE`,
 * sobald er vollständig ist, sonst `DRAFT` (`WorkflowDefinitionService.update`).
 *
 * @returns Tooltip-Text für die gesperrte Kachel oder `null`, wenn der Status nicht sperrt.
 */
export function scheduleBlockedByStatus(status: WorkflowDefinition.StatusEnum | undefined): string | null {
  switch (status) {
    case 'ACTIVE':
      return null;
    case 'DRAFT':
      return 'Erst verfügbar, wenn der Prüflauf vollständig gespeichert ist.';
    case 'INACTIVE':
      return 'Nur für aktive Prüfläufe möglich. Dieser Prüflauf ist inaktiv.';
    case 'ARCHIVED':
      return 'Nur für aktive Prüfläufe möglich. Dieser Prüflauf ist archiviert.';
    default:
      return 'Nur für aktive Prüfläufe möglich.';
  }
}
